import { useCallback, useEffect, useRef, useState, } from 'react';
import { createPortal } from 'react-dom';
var contains = function (eventTarget) { return function (target) { return target.current && target.current.contains(eventTarget); }; };
var usePortal = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.closeOnOutsideClick, closeOnOutsideClick = _c === void 0 ? true : _c, _d = _b.closeOnEsc, closeOnEsc = _d === void 0 ? true : _d, _e = _b.bindTo, bindTo = _e === void 0 ? document && document.body : _e, // attach the portal to this node in the DOM
    _f = _b.isOpen, // attach the portal to this node in the DOM
    defaultIsOpen = _f === void 0 ? false : _f;
    var _g = useState(defaultIsOpen), isOpen = _g[0], setOpen = _g[1];
    var targetEl = useRef(undefined); // this is the element you are clicking/hovering/whatever, to trigger opening the portal
    var portalEl = useRef(document.createElement('div'));
    var openPortal = useCallback(function (event) {
        if (isOpen) {
            return;
        }
        if (event && event.nativeEvent) {
            event.nativeEvent.stopImmediatePropagation();
        }
        if (event) {
            targetEl.current = event.currentTarget;
            if (!targetEl.current) {
                var errorMessage = 'You must either bind to an element or pass an event to openPortal(e).';
                throw new Error(errorMessage);
            }
        }
        setOpen(true);
    }, [isOpen, setOpen, targetEl]);
    var closePortal = useCallback(function () {
        if (isOpen) {
            setOpen(false);
        }
    }, [isOpen, setOpen]);
    var handleKeyDown = useCallback(function (e) {
        var ESC = 27;
        if (e.keyCode === ESC && closeOnEsc) {
            closePortal();
        }
    }, [closeOnEsc, closePortal]);
    var handleMouseDown = useCallback(function (e) {
        var eventTarget = e.target;
        var containsTarget = contains(eventTarget);
        if (containsTarget(portalEl) ||
            e.button !== 0 ||
            !isOpen ||
            !targetEl ||
            containsTarget(targetEl)) {
            return;
        }
        if (closeOnOutsideClick) {
            closePortal();
        }
    }, [isOpen, closePortal, closeOnOutsideClick, portalEl]);
    var handleTouchStart = useCallback(function (e) {
        var eventTarget = e.target;
        var containsTarget = contains(eventTarget);
        if (containsTarget(portalEl) || !isOpen || !targetEl || containsTarget(targetEl)) {
            return;
        }
        if (closeOnOutsideClick) {
            closePortal();
        }
    }, [isOpen, closePortal, closeOnOutsideClick, portalEl]);
    useEffect(function () {
        var portalRootEl = bindTo;
        if (!(portalRootEl instanceof HTMLElement) || !(portalEl.current instanceof HTMLElement)) {
            return function () {
                // no clean up necessary, because we have neither listeners nor children appended
            };
        }
        var node = portalEl.current;
        portalRootEl.appendChild(portalEl.current);
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('touchstart', handleTouchStart);
        return function () {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('touchstart', handleTouchStart);
            portalRootEl.removeChild(node);
        };
    }, [handleTouchStart, handleMouseDown, handleKeyDown, portalEl, bindTo]);
    var Portal = useCallback(function (_a) {
        var children = _a.children;
        if (portalEl.current != null) {
            return createPortal(children, portalEl.current);
        }
        return null;
    }, [portalEl]);
    return {
        Portal: Portal,
        closePortal: closePortal,
        isOpen: isOpen,
        openPortal: openPortal,
    };
};
export default usePortal;
